import { useContext } from 'react'

import { isValidEmail, isValidMobile } from '@domain-group/javascript-utils'

import {
  Button,
  Columns,
  LoadingModule,
  SaveFormModule,
} from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import useAddresses from '@hooks/useAddresses'
import useAgency, { useAgencyForm } from '@hooks/useAgency'
import useAgencySelector from '@hooks/useAgencySelector'

import AgencyContacts from '@components/AgencyContacts'
import NotificationBannerLocation from '@components/NotificationBannerLocation'
import NotificationModuleLocation from '@components/NotificationModuleLocation'

import AgencyDetails from '../AgencyDetails'
import AgencyImages from '../../../../components/AgencyImages'
import AgencyStatistics from '../../../../components/AgencyStatistics'
import PageLayout from '../../../../components/PageLayout'

import AgencyPaymentInformation from '../AgencyPaymentInformation'
import AgencyPaymentMethods from '../AgencyPaymentMethods'
import AgencyPrincipal from '../AgencyPrincipal'
import PhysicalAddress from '../PhysicalAddress'
import ShippingAddresses from '../ShippingAddresses'

const customRequiredFields = [
  { key: 'principal_email', validation: isValidEmail, invalidMessage: 'Invalid Email format' },
  { key: 'principal_mobile', validation: isValidMobile, invalidMessage: 'Invalid Mobile format' },
  { key: 'principal_name' },
]

interface PageContentProps {
  agencyId: number,
}

const PageContent = (props: PageContentProps) => {
  const { agencyId } = props

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      updateAgency,
    },
  } = pageContext

  const { selectedAgencyId } = useAgencySelector()

  const agencyPayload = useAgency({ id: agencyId }, {
    generateUrlsOptions: { prefixRoute: `agencies/${selectedAgencyId}/manage` },
  })

  const useAddressesPayload = useAddresses({
    filters: { owner_id: agencyId, owner_type: 'Agency' },
    performHttpRequests: !!agencyId,
  })

  const { filteredAddresses: addresses } = useAddressesPayload

  const {
    agency,
    callbacks: {
      updateAgency: updateFn,
    },
    loading,
    updating,
  } = agencyPayload

  // @ts-expect-error TS2345
  const agencyFormPayload = useAgencyForm(agency, { customRequiredFields })
  const { entityState, saveEnabled } = agencyFormPayload

  const updateAgencyPayload = {
    callbacks: {
      action: updateFn,
    },
    entityParams: entityState,
    toastText: 'Agency Updated',
  }

  return (
    <PageLayout agencyPayload={agencyPayload} activeTabBarItemKey="details">
      <Columns.Main>
        <NotificationBannerLocation locationKey="agency.agencyManager.details.banner" />

        <LoadingModule loading={loading} times={3} />

        {!loading && (
          <>
            <AgencyDetails agencyFormPayload={agencyFormPayload} />

            <AgencyPrincipal agencyFormPayload={agencyFormPayload} />

            {/*
             // @ts-expect-error TS2322 */}
            <AgencyPaymentInformation agency={agency} agencyFormPayload={agencyFormPayload} />

            {/*
             // @ts-expect-error TS2322 */}
            <PhysicalAddress addresses={addresses} agencyFormPayload={agencyFormPayload} agency={agency} />

            <ShippingAddresses useAddressesPayload={useAddressesPayload} />

            <SaveFormModule>
              <Button
                buttonStyle="primaryCreate"
                disabled={!saveEnabled}
                loading={updating}
                marginLeft="auto"
                onClick={() => updateAgency(updateAgencyPayload)}
                size="medium"
              >
                Save Details
              </Button>
            </SaveFormModule>
          </>
        )}
      </Columns.Main>

      <Columns.Sidebar>
        <NotificationModuleLocation locationKey="agency.agencyManager.details.sidebar" />

        {/*
         // @ts-expect-error TS2322 */}
        <AgencyImages agency={agency} />

        {/*
         // @ts-expect-error TS2322 */}
        <AgencyStatistics agency={agency} />

        <AgencyContacts agencyId={agencyId} />

        <AgencyPaymentMethods agencyId={agencyId} />
      </Columns.Sidebar>
    </PageLayout>
  )
}

export default PageContent
